export interface CapacityInputs {
  readonly teamSize: number;
  readonly minutesPerDay: number;
  readonly workingDays: number;
  readonly hourlyCost: number;
}

export interface CapacityEstimate {
  readonly hoursPerYear: number;
  readonly fullTimeEquivalents: number;
  readonly hoursPerPerson: number;
  readonly value: number;
}

/** Full-time equivalent assumes 8-hour working days (stated on the page). */
const HOURS_PER_WORKING_DAY = 8;

export const INPUT_LIMITS = {
  teamSize: { min: 0, max: 10000 },
  minutesPerDay: { min: 0, max: 120 },
  workingDays: { min: 0, max: 366 },
  hourlyCost: { min: 0, max: 10000 },
} as const satisfies Record<keyof CapacityInputs, { min: number; max: number }>;

/** Parses a number input; empty or invalid input counts as 0, and values are clamped to the allowed range. */
export function parseBoundedNumber(raw: string, { min, max }: { min: number; max: number }): number {
  const value = parseFloat(raw);
  return Number.isNaN(value) ? 0 : Math.min(max, Math.max(min, value));
}

export function estimateCapacity({
  teamSize,
  minutesPerDay,
  workingDays,
  hourlyCost,
}: CapacityInputs): CapacityEstimate {
  const hoursPerPerson = (minutesPerDay / 60) * workingDays;
  const hoursPerYear = teamSize * hoursPerPerson;
  return {
    hoursPerYear,
    fullTimeEquivalents: workingDays ? hoursPerYear / (workingDays * HOURS_PER_WORKING_DAY) : 0,
    hoursPerPerson,
    value: hoursPerYear * hourlyCost,
  };
}

export const formatNumber = (n: number, fractionDigits = 0): string =>
  n.toLocaleString('en-US', { maximumFractionDigits: fractionDigits, minimumFractionDigits: fractionDigits });

/** Summary handed to the demo form's message field when the visitor continues from the calculator. */
export function prefillMessage(inputs: CapacityInputs, estimate: CapacityEstimate): string {
  return `Capacity calculator: ${formatNumber(inputs.teamSize)} people × ${inputs.minutesPerDay} min/day × ${formatNumber(inputs.workingDays)} days ≈ ${formatNumber(Math.round(estimate.hoursPerYear))} hours per year (${formatNumber(estimate.fullTimeEquivalents, 1)} FTE).`;
}
